import { useEffect } from 'react';
import type { ReactNode } from 'react';
import type { ReportStatus } from '../lib/api';

const STATUS_STYLES: Record<ReportStatus, string> = {
  draft: 'bg-slate-100 text-slate-700',
  submitted: 'bg-amber-100 text-amber-800',
  changes_requested: 'bg-orange-100 text-orange-800',
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-800',
  paid: 'bg-brand-light text-brand',
};

export function StatusPill({ status }: { status: ReportStatus }) {
  return (
    <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${STATUS_STYLES[status]}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

export function Money({ value, className = '' }: { value: string | number; className?: string }) {
  const n = typeof value === 'number' ? value : Number(value);
  return (
    <span className={`tabular-nums ${className}`}>
      {n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
    </span>
  );
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-3 p-8 text-slate-500">
      <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-brand" />
      {label}
    </div>
  );
}

export function ErrorNote({ children }: { children: ReactNode }) {
  if (!children) return null;
  return (
    <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
      {children}
    </div>
  );
}

export function EmptyState({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="card p-8 text-center">
      <p className="font-medium text-slate-700">{title}</p>
      {hint ? <p className="mt-1 text-sm text-slate-500">{hint}</p> : null}
      {action ? <div className="mt-4 flex justify-center">{action}</div> : null}
    </div>
  );
}

/**
 * A bottom sheet on a phone, a centred dialog from `lg:` up. One component so
 * the line editor does not need two implementations.
 */
export function Sheet({
  open, onClose, title, children, footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  // Escape closes the dialog. Expected behaviour for anything modal, and the
  // only keyboard way out on desktop -- without it the sole exit is the small
  // x or a click on the backdrop.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Stop the page behind a sheet from scrolling, which on a phone otherwise
  // drags the content underneath while the sheet stays put.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [open]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 lg:items-center" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="flex max-h-[92vh] w-full flex-col rounded-t-2xl bg-white shadow-xl lg:max-w-lg lg:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 className="text-base font-semibold">{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="min-h-11 px-2 text-2xl leading-none text-slate-400">
            &times;
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-4 py-4">{children}</div>
        {footer ? (
          <footer
            className="border-t border-slate-200 px-4 pt-3"
            style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
          >
            {footer}
          </footer>
        ) : null}
      </div>
    </div>
  );
}

/** Desktop-only notice for the finance/admin screens. */
export function DesktopOnlyNotice() {
  return (
    <div className="card mb-4 p-4 lg:hidden">
      <p className="text-sm text-slate-600">
        This screen is built for a desktop. You can read it here, but reviewing and bulk actions are
        much easier on a larger screen.
      </p>
    </div>
  );
}
